import { Controller } from "@hotwired/stimulus"

// Controls the "Schedule Meeting" form on the adjuster meetings index page.
//
// This controller is attached to three distinct elements that cooperate via the
// shared `#meeting-form-container` element and the `hidden` Tailwind class:
//   1. The "Schedule Meeting" trigger button (action: toggle)
//   2. The form container div (target: container)
//   3. The form itself (targets: addressField, linkField; actions: cancel,
//      toggleLocationFields)
//
// Because the trigger button and the form container are sibling instances, the
// container is resolved by id when the in-scope target is not available.
export default class extends Controller {
  static targets = ["container", "addressField", "linkField"]

  // Location types that reveal an extra field. Everything else (property,
  // phone) hides both extra fields.
  static LOCATION_FIELD_MAP = {
    other: "addressField",
    virtual: "linkField"
  }

  // Show/hide the meeting form. Wired to the "Schedule Meeting" button, which
  // is a separate controller instance from the container, so fall back to the
  // shared element id when this instance has no container target.
  toggle() {
    const container = this.container
    if (!container) return

    container.classList.toggle("hidden")

    if (!container.classList.contains("hidden")) {
      this.scrollIntoView(container)
      this.focusFirstField(container)
    }
  }

  // Hide and reset the form. Wired to the form's Cancel button, which lives in
  // the form instance, so the container is resolved by id.
  cancel() {
    const container = this.container
    if (container) {
      container.classList.add("hidden")
    }

    const form = this.element.matches("form") ? this.element : this.element.querySelector("form")
    if (form) form.reset()

    // Reset() restores the original location_type selection, so re-evaluate
    // which optional field should be visible.
    this.applyLocationFields(this.currentLocationType())
  }

  // Show the address field for "other" locations and the meeting-link field for
  // "virtual" meetings. Wired to the location_type select's change event.
  toggleLocationFields(event) {
    const value = event?.target?.value ?? this.currentLocationType()
    this.applyLocationFields(value)
  }

  // --- helpers -------------------------------------------------------------

  applyLocationFields(value) {
    const visibleTarget = this.constructor.LOCATION_FIELD_MAP[value]

    if (this.hasAddressFieldTarget) {
      this.addressFieldTarget.classList.toggle("hidden", visibleTarget !== "addressField")
    }
    if (this.hasLinkFieldTarget) {
      this.linkFieldTarget.classList.toggle("hidden", visibleTarget !== "linkField")
    }
  }

  // Resolve the container: prefer the in-scope Stimulus target, otherwise fall
  // back to the shared element id so cross-instance actions still work.
  get container() {
    if (this.hasContainerTarget) return this.containerTarget
    return document.getElementById("meeting-form-container")
  }

  // Read the current location_type from the rendered select. Used when no
  // event is available (e.g. after a form reset).
  currentLocationType() {
    const select = this.element.querySelector("select[name*='location_type']")
    return select ? select.value : null
  }

  focusFirstField(container) {
    const field = container.querySelector("input, select, textarea")
    if (field) field.focus()
  }

  scrollIntoView(container) {
    container.scrollIntoView({ behavior: "smooth", block: "start" })
  }
}
